import React, { HTMLProps, useEffect, useRef } from 'react';
import Chart from 'chart.js';
import { Fund } from '../../../api/robotapi';
import { COLORS } from '../../../config/constants';
import { useWindowBreakpoints } from '../../../hooks/useWindowBreakpoints';

Chart.defaults.global.defaultFontFamily = 'museo-sans, sans-serif';

type ForecastFundsChartDataSet = Chart.ChartDataSets & {
    id: string;
};

export const ForecastFundsChart: React.FC<{ stocks: Fund[]; bonds: Fund[] } & HTMLProps<HTMLDivElement>> = ({
    className,
    stocks,
    bonds,
    ...rest
}) => {
    const breakpoints = useWindowBreakpoints();
    const canvasRef = useRef<HTMLCanvasElement>(undefined!);
    const chartRef = useRef<Chart>(null);
    const dataSetRef = useRef<ForecastFundsChartDataSet[]>([]);

    useEffect(() => {
        if (!canvasRef.current) return;

        if (!chartRef.current) {
            chartRef.current = new Chart(canvasRef.current, {
                type: 'pie',
                data: {
                    datasets: dataSetRef.current,
                },
                plugins: [],
                options: {
                    animation: {
                        duration: 0,
                    },
                    tooltips: {
                        titleFontSize: 9,
                        bodyFontSize: 9,
                        callbacks: {
                            label: function (tooltipItem, data) {
                                if (tooltipItem.datasetIndex !== undefined && tooltipItem.index !== undefined) {
                                    let value =
                                        data?.datasets?.[tooltipItem.datasetIndex]?.data?.[tooltipItem.index] || '';
                                    let label = data?.labels?.[tooltipItem.index] || '';
                                    return `${label}: ${value}%`;
                                }
                                return '';
                            },
                        },
                    },
                    // responsive: true,
                    maintainAspectRatio: false,
                    legend: {
                        display: false,
                    },
                    cutoutPercentage: 70,
                },
            });
        }

        addLabelsToChart(chartRef.current, stocks, bonds);
    }, [canvasRef, stocks, bonds, breakpoints]);

    return (
        <div className={`relative flex-1 z-10 ${className}`} {...rest}>
            <canvas ref={canvasRef}></canvas>
        </div>
    );
};

const addLabelsToChart = (chart: Chart, shares: Fund[], obligates: Fund[]) => {
    chart.data.labels = [...obligates.map((o) => o.name), ...shares.map((s) => s.name)];
    chart.data.datasets = [
        {
            data: [...obligates.map((o) => o.weight), ...shares.map((s) => s.weight)],
            backgroundColor: [
                ...obligates.map((o, i) => COLORS.obligates[i]),
                ...shares.map((s, i) => COLORS.shares[i]),
            ],
        },
    ];
    chart.update();
};
